import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useQuery } from '@root/hooks/useQuery';
import './style.scss';

export const TitleTab = ({
  tabs,
  setTabs,
  items = [],
  isIcon = false,
  iconMap = {},
  containerClasses = {},
  onTabClick = () => {},
  // New props for URL state management
  urlParamName = 'currentTab',
  validTabs = [],
  defaultTab = null,
  enableUrlState = false,
  onTabChange = null, // Additional callback for tab changes
  // Icon type configuration
  iconType = 'image', // 'image' | 'component' | 'mixed'
  iconProps = {}, // Additional props for icons (like className, style, etc.)
}) => {
  const history = useHistory();
  const query = useQuery();

  // Get tab from URL
  const getTabFromUrl = useCallback(() => {
    if (!enableUrlState) return tabs;
    const tabParam = query.get(urlParamName);
    const validTabValues = validTabs.length > 0 ? validTabs : items.map(item => item.value);
    return validTabValues.includes(tabParam) ? tabParam : tabs || defaultTab || items[0]?.value;
  }, [query, urlParamName, validTabs, items, defaultTab, enableUrlState, tabs]);

  // Update URL with tab
  const updateUrlTab = useCallback(
    tab => {
      if (!enableUrlState) return;
      const currentUrl = new URL(window.location);
      if (currentUrl.searchParams.get(urlParamName) !== tab.toString()) {
        currentUrl.searchParams.set(urlParamName, tab.toString());
        history.replace(`${currentUrl.pathname}${currentUrl.search}`);
      }
    },
    [history, urlParamName, enableUrlState],
  );

  // Render icon based on type
  const renderIcon = useCallback(
    (iconKey, value, label) => {
      const iconData = iconMap[iconKey || value];

      if (!iconData) return null;

      // Default icon styling
      const defaultIconStyle = {
        objectFit: 'contain',
        marginRight: '5px',
        display: 'inline-flex',
        alignItems: 'center',
      };

      const defaultIconSize = {
        width: 24,
        height: 15,
      };

      // Mixed type: check if iconData is a component or string
      if (iconType === 'mixed') {
        // If it's a React component (function or class)
        if (typeof iconData === 'function' || (iconData && typeof iconData === 'object' && iconData.$$typeof)) {
          const IconComponent = iconData;
          return (
            <div style={{ marginRight: '5px', display: 'inline-flex', alignItems: 'center' }}>
              <IconComponent {...iconProps} />
            </div>
          );
        }
        // If it's a string (URL/path)
        else if (typeof iconData === 'string') {
          return (
            <img
              src={iconData}
              {...defaultIconSize}
              alt={label}
              style={{
                ...defaultIconStyle,
                ...iconProps.style,
              }}
              {...iconProps}
            />
          );
        }
      }
      // Component type
      else if (iconType === 'component') {
        const IconComponent = iconData;
        return (
          <div style={{ marginRight: '5px', display: 'inline-flex', alignItems: 'center' }}>
            <IconComponent {...iconProps} />
          </div>
        );
      }
      // Image type (default)
      else {
        return (
          <img
            src={iconData}
            {...defaultIconSize}
            alt={label}
            style={{
              ...defaultIconStyle,
              ...iconProps.style,
            }}
            {...iconProps}
          />
        );
      }

      return null;
    },
    [iconMap, iconType, iconProps],
  );

  // Handle tab click
  const handleTabClick = useCallback(
    (e, value) => {
      e.preventDefault();
      setTabs(value);
      if (enableUrlState) {
        updateUrlTab(value);
      }
      onTabClick(value);
      if (onTabChange) {
        onTabChange(value);
      }
    },
    [setTabs, updateUrlTab, onTabClick, onTabChange, enableUrlState],
  );

  // Sync with URL on mount and URL changes
  useEffect(() => {
    if (enableUrlState) {
      const tabFromUrl = getTabFromUrl();
      if (tabFromUrl !== tabs && query.get(urlParamName)) {
        setTabs(tabFromUrl);
        if (onTabChange) {
          onTabChange(tabFromUrl);
        }
      }
    }
  }, [getTabFromUrl, tabs, setTabs, onTabChange, enableUrlState, query, urlParamName]);

  // Handle browser back/forward navigation
  useEffect(() => {
    if (!enableUrlState) return;

    const handlePopState = () => {
      const tabFromUrl = getTabFromUrl();
      if (tabFromUrl !== tabs) {
        setTabs(tabFromUrl);
        if (onTabChange) {
          onTabChange(tabFromUrl);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [getTabFromUrl, tabs, setTabs, onTabChange, enableUrlState]);

  return (
    <div className="CommonTitleTab__Container" style={containerClasses}>
      {items.map(({ label, value, iconKey }) => (
        <div
          key={value}
          style={{ height: '100%' }}
          className={`CommonTitleTab ${tabs === value ? 'CommonActiveTab' : ''}`}
          onClick={e => handleTabClick(e, value)}>
          {isIcon && renderIcon(iconKey, value, label)}
          {label}
        </div>
      ))}
    </div>
  );
};
